import { useCallback, useEffect, useState } from 'react';
import {
  FormDataConsumer,
  RadioButtonGroupInput,
  ReferenceInput,
  SelectInput,
  TextInput,
  required,
  useEditContext,
} from 'react-admin';

import { useFormLanguage } from '@app/hooks/useFormLanguage';
import { getTomorrowsDate } from '@app/utils/dateUtils';
import { DEFAULT_LANGUAGE } from '@app/utils/languages';
import {
  AUTOMATED_RAIL_TYPE,
  CONFIGURATION_TYPE,
  ORIENTATION_TYPE,
  PAGE_TYPE,
  PODCAST_TYPE,
  SECTION_TYPE,
} from '@app/utils/railsUtils';
import AddIcon from '@mui/icons-material/Add';
import { Button, Grid, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { DialogAddMedia } from '@app/components/DialogAddMedia';
import { LabelNotClickableBoolInput } from '@app/components/LabelNotClickableBoolInput';
import { RailsCarousel } from '@app/components/RailsCarousel';
import { CustomDateInput } from '@app/components/common/CustomDateInput';
import { LanguageSelector } from '@app/components/common/LanguageSelector';
import { MultiLingualField } from '@app/components/common/MultiLingualField';
import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

interface RailFormProps {
  title: string;
  editing?: boolean;
  selected: GenericObject[];
  type: string | null;
  handleChangeSelected(values: GenericObject[]): void;
  handleChangeFirstRenderSelected(values: GenericObject[]): void;

  moveCard(dragIndex: number, hoverIndex: number): void;
}

export const RailForm = ({
  title,
  selected,
  handleChangeSelected,
  handleChangeFirstRenderSelected,
  moveCard,
  editing = false,
  type,
}: RailFormProps) => {
  const [isAddMediaOpen, setIsAddMediaOpen] = useState(false);
  const [isMediaDefined, setIsMediaDefined] = useState(false);
  const { language, handleChangeLanguage } = useFormLanguage();
  const { refetch } = useEditContext();
  const handleCloseAddMedia = () => {
    setIsAddMediaOpen(false);
  };

  const deleteCard = (id: number) => {
    const newSelected = selected.filter((item: GenericObject) => item.id !== id);
    handleChangeSelected(newSelected);

    return newSelected;
  };

  const updateRecord = useCallback(async () => {
    if (refetch && !isMediaDefined) {
      const response = await refetch();
      const railItems = response?.data?.rail_items
        ? response?.data.rail_items.map((item: GenericObject) => ({
            id: item.content_id,
            title: item.content_title,
            vod_file_base: { id: 0, file_name: '', path: item.image_url },
            vod_url: item.vod_url,
            date_created: item.date_created,
          }))
        : [];
      handleChangeSelected(railItems);
      handleChangeFirstRenderSelected(JSON.parse(JSON.stringify(railItems)));
      setIsMediaDefined(true);
    }
  }, [refetch, isMediaDefined, handleChangeSelected, handleChangeFirstRenderSelected]);

  useEffect(() => {
    updateRecord();
  }, [updateRecord]);

  const getSectionTypes = (formData: any) => {
    const sectionTypes = [
      { id: SECTION_TYPE.RAIL, name: 'Rail' },
      { id: SECTION_TYPE.CTA, name: 'CTA' },
    ];

    if (type) {
      formData.page_type = type;
    }

    if (formData.page_type === PAGE_TYPE.DISCOVER) {
      sectionTypes.push({ id: SECTION_TYPE.PODCAST, name: 'Podcast' });
    }

    return sectionTypes;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormDataConsumer>
        {({ formData }) => (
          <div>
            <Typography data-cy={`edit-${title}`} variant="h3" sx={{ marginBottom: 2 }}>
              {title}
            </Typography>
            <LanguageSelector language={language} handleChangeLanguage={handleChangeLanguage} />

            <Grid spacing={2} xl={8} lg={8} md={12} xs={12} container item>
              <Grid item xs={12}>
                <MultiLingualField
                  Component={TextInput}
                  language={language}
                  source="title"
                  label="Name"
                  helperText={false}
                  variant="outlined"
                  fullWidth
                  validate={[required()]}
                />
              </Grid>
              <Grid item xs={12}>
                <SelectInput
                  source="section_type"
                  variant="outlined"
                  helperText={false}
                  disabled={editing}
                  label="Section Type"
                  choices={getSectionTypes(formData)}
                  fullWidth
                  validate={[required()]}
                />
              </Grid>
              {formData.section_type === SECTION_TYPE.RAIL && (
                <Grid item xs={12}>
                  <SelectInput
                    source="configuration_type"
                    variant="outlined"
                    helperText={false}
                    label="Rail Type"
                    disabled={editing}
                    choices={[
                      { id: CONFIGURATION_TYPE.MANUAL, name: 'Manual' },
                      { id: CONFIGURATION_TYPE.AUTO, name: 'Auto' },
                    ]}
                    fullWidth
                    validate={[required()]}
                  />
                </Grid>
              )}
              {formData.section_type === SECTION_TYPE.CTA && (
                <Grid item xs={12}>
                  <ReferenceInput source="cta_id" reference="ctas">
                    <SelectInput
                      variant="outlined"
                      helperText={false}
                      label="CTA"
                      validate={[required()]}
                      fullWidth
                      optionText={(record) => record.heading[DEFAULT_LANGUAGE]}
                    />
                  </ReferenceInput>
                </Grid>
              )}
              {formData.section_type === SECTION_TYPE.PODCAST && (
                <Grid item xs={12}>
                  <Grid item xs={12}>
                    <SelectInput
                      source="podcast_type"
                      variant="outlined"
                      helperText={false}
                      label="Podcast Type"
                      disabled={editing}
                      choices={[
                        { id: PODCAST_TYPE.LATEST, name: 'Latest' },
                        { id: PODCAST_TYPE.FEATURED, name: 'Featured' },
                      ]}
                      fullWidth
                      validate={[required()]}
                    />
                  </Grid>
                </Grid>
              )}
              {formData.section_type === SECTION_TYPE.PODCAST && formData.podcast_type === PODCAST_TYPE.FEATURED && (
                <Grid item xs={12}>
                  <TextInput
                    source="podcast_id"
                    label="Podcast Id"
                    helperText={false}
                    variant="outlined"
                    fullWidth
                    validate={[required()]}
                  />
                </Grid>
              )}
              {!!(
                formData.section_type === SECTION_TYPE.RAIL && formData.configuration_type === CONFIGURATION_TYPE.MANUAL
              ) && (
                <>
                  <Grid item xs={12}>
                    <Button variant="contained" onClick={() => setIsAddMediaOpen(true)} startIcon={<AddIcon />}>
                      ADD MEDIA
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <RailsCarousel deleteCard={deleteCard} selected={selected} moveCard={moveCard} />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <SelectInput
                  source="orientation_type"
                  variant="outlined"
                  helperText={false}
                  defaultValue={ORIENTATION_TYPE.HORIZONTAL}
                  label="Mobile App Orientation"
                  choices={[
                    { id: ORIENTATION_TYPE.HORIZONTAL, name: 'Horizontal' },
                    { id: ORIENTATION_TYPE.VERTICAL, name: 'Vertical' },
                    { id: ORIENTATION_TYPE.SPOTLIGHT, name: 'Spotlight' },
                  ]}
                  fullWidth
                  validate={[required()]}
                />
              </Grid>
              {!!(
                formData.section_type === SECTION_TYPE.RAIL && formData.configuration_type === CONFIGURATION_TYPE.AUTO
              ) && (
                <Grid item xs={12}>
                  <RadioButtonGroupInput
                    label="Criteria for Auto Rail"
                    helperText={false}
                    source="automated_rail_type"
                    fullWidth
                    validate={[required()]}
                    choices={[
                      { id: AUTOMATED_RAIL_TYPE.LATEST, name: 'Latest' },
                      { id: AUTOMATED_RAIL_TYPE.TOPIC_DRIVEN, name: 'Topic Driven' },
                      { id: AUTOMATED_RAIL_TYPE.SERIES_DRIVEN, name: 'Series Driven' },
                    ]}
                  />
                </Grid>
              )}
              {!!(
                formData.section_type === SECTION_TYPE.RAIL &&
                formData.configuration_type === CONFIGURATION_TYPE.AUTO &&
                formData.automated_rail_type === AUTOMATED_RAIL_TYPE.TOPIC_DRIVEN
              ) && (
                <Grid item xs={12}>
                  <ReferenceInput source="taxonomy_id" reference="taxonomies">
                    <SelectInput
                      variant="outlined"
                      helperText={false}
                      label="Topic"
                      validate={[required()]}
                      fullWidth
                      optionText={(record) => record.category_name[DEFAULT_LANGUAGE]}
                    />
                  </ReferenceInput>
                </Grid>
              )}
              {!!(
                formData.section_type === SECTION_TYPE.RAIL &&
                formData.configuration_type === CONFIGURATION_TYPE.AUTO &&
                formData.automated_rail_type === AUTOMATED_RAIL_TYPE.SERIES_DRIVEN
              ) && (
                <Grid item xs={12}>
                  <ReferenceInput source="event_series_id" reference="series">
                    <SelectInput
                      variant="outlined"
                      helperText={false}
                      label="Series"
                      validate={[required()]}
                      fullWidth
                      optionText={(record) => record.name[DEFAULT_LANGUAGE]}
                    />
                  </ReferenceInput>
                </Grid>
              )}
              {formData.section_type === SECTION_TYPE.RAIL && (
                <Grid item xs={6}>
                  <TextInput
                    helperText={false}
                    variant="outlined"
                    label="Maximum VODs visible in rail"
                    type="number"
                    validate={formData.section_type === SECTION_TYPE.RAIL ? [required()] : []}
                    source="number_of_vods"
                    defaultValue={10}
                    fullWidth
                  />
                </Grid>
              )}
              <Grid item xs={6}>
                <CustomDateInput source="expiry_date" label="Expiry Date" minDate={getTomorrowsDate()} />
              </Grid>
              <Grid item xs={12}>
                <LabelNotClickableBoolInput label="Published" source="is_published" />
              </Grid>
              <DialogAddMedia
                open={isAddMediaOpen}
                handleClose={handleCloseAddMedia}
                selected={selected}
                handleChangeSelected={handleChangeSelected}
              />
            </Grid>
          </div>
        )}
      </FormDataConsumer>
    </LocalizationProvider>
  );
};
